import { Download, Globe, Mail, MapPin, Waves } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { PATCH_URL } from "../data/content";

export default function Closing() {
  const goTo = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <section id="contact" data-testid="system-closing-banner" className="relative py-28 lg:py-40 overflow-hidden bg-abyss/60">
        <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none" />
        <div className="absolute inset-0 vignette pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <div className="font-mono text-[11px] tracking-[0.35em] text-polar mb-8">ARJUNA ANTARCTICA · PROPOSED</div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[0.9] text-[11vw] sm:text-[8vw] lg:text-7xl xl:text-8xl text-ice">
              The Earth is <span className="text-stroke">one system.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 max-w-3xl mx-auto text-base sm:text-lg text-ice/70 leading-relaxed">
              From polar ice to tropical oceans, from satellite observations to decisions on the ground, understanding
              our planet requires better data and better questions.
            </p>
            <p className="mt-4 max-w-3xl mx-auto text-base sm:text-lg text-ice/70 leading-relaxed">
              Arjuna Antarctica is a proposed step towards building that intelligence.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
              <a
                href="#partners"
                onClick={goTo("#partners")}
                data-testid="closing-cta-partner"
                className="bg-mission text-ice font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 clip-corner hover:bg-ice hover:text-midnight transition-colors duration-300"
              >
                Partner With Arjuna
              </a>
              <a
                href="#rationale"
                onClick={goTo("#rationale")}
                data-testid="closing-cta-capabilities"
                className="border border-polar/40 text-polar font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 clip-corner-tl hover:border-polar hover:bg-polar/10 transition-colors duration-300"
              >
                Explore Our Earth Intelligence Capabilities
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto text-left" data-testid="contact-details">
              <div className="border border-polar/15 bg-midnight/60 p-6">
                <MapPin size={18} className="text-polar mb-3" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.25em] text-tech mb-1.5">HEADQUARTERS</div>
                <p className="text-sm text-ice/80">Arjuna Space AI India<br />Pune, Maharashtra, India</p>
              </div>
              <div className="border border-polar/15 bg-midnight/60 p-6">
                <Globe size={18} className="text-polar mb-3" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.25em] text-tech mb-1.5">WEB</div>
                <a href="https://www.arjunaspace.in" target="_blank" rel="noopener noreferrer" className="text-sm text-polar hover:text-ice transition-colors" data-testid="contact-website-link">
                  www.arjunaspace.in
                </a>
              </div>
              <div className="border border-polar/15 bg-midnight/60 p-6">
                <Mail size={18} className="text-polar mb-3" strokeWidth={1.5} />
                <div className="font-mono text-[10px] tracking-[0.25em] text-tech mb-1.5">ENQUIRIES</div>
                <p className="text-sm text-ice/80">
                  Via the{" "}
                  <a href="#partners" onClick={goTo("#partners")} className="text-polar hover:text-ice transition-colors">
                    partnership form
                  </a>
                  . Mission brief and slide deck available below.
                </p>
              </div>
            </div>

            <div className="mt-6 max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 text-left" data-testid="download-section">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/download/brief.pdf`}
                data-testid="download-pdf-button"
                className="group flex items-center justify-between gap-4 border border-gold/40 bg-gold/10 px-6 py-5 hover:bg-gold/20 hover:border-gold/70 transition-colors duration-300"
              >
                <span>
                  <span className="block font-mono text-[9px] tracking-[0.3em] text-gold">PDF · FULL WEBSITE CAPTURE</span>
                  <span className="block font-display font-semibold text-xl text-ice mt-1.5 tracking-wide">Download Website as PDF</span>
                </span>
                <Download size={20} className="text-gold shrink-0 transition-transform duration-300 group-hover:translate-y-1" />
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/download/deck.pptx`}
                data-testid="download-ppt-button"
                className="group flex items-center justify-between gap-4 border border-polar/40 bg-polar/10 px-6 py-5 hover:bg-polar/20 hover:border-polar/70 transition-colors duration-300"
              >
                <span>
                  <span className="block font-mono text-[9px] tracking-[0.3em] text-polar">PPTX SLIDE DECK · 11 SLIDES</span>
                  <span className="block font-display font-semibold text-xl text-ice mt-1.5 tracking-wide">Download Mission Deck</span>
                </span>
                <Download size={20} className="text-polar shrink-0 transition-transform duration-300 group-hover:translate-y-1" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.35}>
            <Link
              to="/ocean"
              data-testid="closing-ocean-link"
              className="group mt-6 max-w-4xl mx-auto flex items-center justify-between gap-4 border border-polar/40 bg-gradient-to-r from-[#082E49]/60 to-[#0F7194]/30 px-6 py-6 hover:border-polar transition-colors duration-300"
            >
              <span className="flex items-center gap-4 text-left">
                <Waves size={24} className="text-polar shrink-0" strokeWidth={1.6} />
                <span>
                  <span className="block font-mono text-[9px] tracking-[0.3em] text-polar">COMPANION MISSION · ARJUNA INDIAN OCEAN EXPEDITION</span>
                  <span className="block font-display font-semibold text-xl sm:text-2xl text-ice mt-1.5 tracking-wide">Dive India. Map Tomorrow. — Explore the Underwater Mission</span>
                </span>
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-polar group-hover:translate-x-1 transition-transform duration-300 shrink-0 hidden sm:inline">Enter →</span>
            </Link>
          </Reveal>
        </div>
      </section>

      <footer className="relative border-t border-polar/10 bg-abyss" data-testid="site-footer">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5 flex items-start gap-4">
              <img src={PATCH_URL} alt="Arjuna Antarctica mission patch" className="w-14 h-14 rounded-full ring-1 ring-gold/40 shrink-0" />
              <div>
                <div className="font-display font-bold text-2xl text-ice tracking-wide">ARJUNA SPACE AI INDIA</div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-gold mt-1.5">SAME PLANET. BIGGER QUESTIONS.</div>
                <a href="https://www.arjunaspace.in" target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-polar hover:text-ice transition-colors mt-2 inline-block" data-testid="footer-website-link">
                  https://www.arjunaspace.in
                </a>
              </div>
            </div>

            <div className="lg:col-span-4" data-testid="footer-environmental-statement">
              <div className="font-mono text-[10px] tracking-[0.25em] text-tech mb-3">ENVIRONMENTAL RESPONSIBILITY</div>
              <p className="text-xs text-ice/55 leading-relaxed">
                Any proposed field activity will follow expedition operator rules, applicable authorisations under the
                Antarctic Treaty System, biosecurity protocols, wildlife-protection and approach-distance rules, and
                protected-area regulations. No field activity will occur without the required permissions.
              </p>
            </div>

            <div className="lg:col-span-3" data-testid="footer-coordinates">
              <div className="font-mono text-[10px] tracking-[0.25em] text-tech mb-3">TELEMETRY</div>
              <div className="font-mono text-[11px] text-ice/60 space-y-1.5 tracking-wider">
                <div>STUDY REGION — ~63°S 059°W</div>
                <div>BASE — 18°31′N 073°51′E</div>
                <div>WINDOW — DEC 2027–2029 (TARGET)</div>
                <div className="text-tech">STATUS — DEVELOPMENT PHASE</div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-polar/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="footer-links">
            <p className="font-mono text-[10px] tracking-[0.2em] text-tech">
              © 2026 ARJUNA SPACE AI INDIA. A PROPOSED MISSION — NOTHING HERE IS A CONFIRMED RESULT, PARTNERSHIP OR AUTHORISATION.
            </p>
            <div className="flex gap-6 font-mono text-[10px] tracking-[0.2em]">
              <a href="#partners" onClick={goTo("#partners")} className="text-tech hover:text-polar transition-colors">PARTNER</a>
              <a href="#team" onClick={goTo("#team")} className="text-tech hover:text-polar transition-colors">TEAM</a>
              <a href="/updates" className="text-tech hover:text-polar transition-colors">UPDATES</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
